import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Authorized from 'containers/authorized-container';
import { authenticateAcrossAllGeos } from 'api/api-authentication';
import { getAPIClient } from 'api/api-common';
import PropTypes from 'prop-types';

const ProtectedRoute = ({ component: Component }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!window.AuthenticationContext) {
      Object.keys(window.TIEOUT.ENV.GEOS).forEach((geoKey) =>
        getAPIClient(geoKey),
      );
    }
    authenticateAcrossAllGeos().then(() => setAuthenticated(true));
  }, []);

  // Memoize the router object to prevent recreation on every render
  // Don't depend on location at all to avoid re-renders from history manipulation
  const router = useMemo(
    () => ({
      navigate,
      location,
      push: (path) => navigate(path),
      replace: (path) => navigate(path, { replace: true }),
    }),
    [navigate],
  );

  const ComponentWithRouterProps = useCallback(
    (props) => {
      return (
        <Component
          {...props}
          params={params}
          navigate={navigate}
          location={location}
          router={router}
        />
      );
    },
    [Component],
  );

  if (authenticated) {
    return <Authorized component={ComponentWithRouterProps} />;
  } else {
    return null;
  }
};

ProtectedRoute.propTypes = {
  /**React component to be rendered in the protected route component */
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
};

const arePropsEqual = (prevProps, nextProps) => {
  return prevProps.component === nextProps.component;
};

export default memo(ProtectedRoute, arePropsEqual);
